import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


export type DueStatus = 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE' | 'WAIVED' | 'WRITTEN_OFF';
export type PaymentMode = 'CASH' | 'UPI' | 'NEFT' | 'CHEQUE' | 'ADJUSTMENT';

export interface LastReceipt {
  receiptId: string;
  receiptNumber: string;
  amount: number;
  paymentMode: PaymentMode;
  collectedAt: string;
  status: 'ACTIVE' | 'CANCELLED';
}

export interface DueRow {
  id: string;
  subscriberId: string;
  schemeId: string;
  cycleId: string;
  baseAmount: number;
  dividendCredit: number;
  netDue: number;
  dueDate: string;
  status: DueStatus;
  paidAmount: number;
  overdueAt: string | null;
  createdAt: string;
  lastReceipt: LastReceipt | null;
}

export interface CycleSummary {
  totalDue: number;
  totalCollected: number;
  outstandingCount: number;
}

export interface RecordPaymentBody {
  amount: number;
  paymentMode: PaymentMode;
  upiRef?: string;
  chequeNumber?: string;
  chequeDate?: string;
  bankName?: string;
  idempotencyKey: string;
}

export interface RecordPaymentResponse {
  receiptId: string;
  receiptNumber: string;
}

export const duesKeys = {
  cycleDues: (schemeId: string, cycleNum: number, status?: string) =>
    ['dues', 'cycle', schemeId, cycleNum, status] as const,
  cycleSummary: (schemeId: string, cycleNum: number) =>
    ['dues', 'summary', schemeId, cycleNum] as const,
};

export const receiptsKeys = {
  all: ['receipts'] as const,
  scheme: (schemeId: string) => [...receiptsKeys.all, 'scheme', schemeId] as const,
};

export function useCycleSummary(schemeId: string, cycleNum: number) {
  return useQuery({
    queryKey: duesKeys.cycleSummary(schemeId, cycleNum),
    queryFn: () =>
      fetcher.get<CycleSummary>(`/staff/v1/dues/schemes/${schemeId}/cycles/${cycleNum}/dues/summary`),
    enabled: !!schemeId && cycleNum > 0,
  });
}

export function useCycleDues(
  schemeId: string,
  cycleNum: number,
  page: number,
  status?: string,
) {
  return useQuery({
    queryKey: [...duesKeys.cycleDues(schemeId, cycleNum, status), page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (status) params.set('status', status);
      return fetcher.get<{ data: DueRow[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/dues/schemes/${schemeId}/cycles/${cycleNum}/dues?${params}`,
      );
    },
    enabled: !!schemeId && cycleNum > 0,
  });
}

export function useSchemeReceipts(schemeId: string, page: number) {
  return useQuery({
    queryKey: [...receiptsKeys.scheme(schemeId), page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      return fetcher.get<{ data: any[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/dues/schemes/${schemeId}/receipts?${params}`,
      );
    },
    enabled: !!schemeId,
  });
}

export function useRecordPayment(dueId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<RecordPaymentBody, 'idempotencyKey'>) =>
      fetcher.post<RecordPaymentResponse>(`/staff/v1/dues/dues/${dueId}/receipts`, {
        ...body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dues'] });
      qc.invalidateQueries({ queryKey: ['receipts'] });
    },
  });
}

export function useCancelReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receiptId, reason }: { receiptId: string; reason: string }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/dues/receipts/${receiptId}/cancel`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dues'] });
      qc.invalidateQueries({ queryKey: ['receipts'] });
    },
  });
}
